import { requireTwoFactorAuthentication } from 'actions/twofactor.actions';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { formatAsFullWithTime } from 'helpers/dateHelper';
import { openModalWithUrlReplace } from 'helpers/modal.helper';
import { formatIncome, formatNegativeIncome } from 'helpers/money.helper';
import { useComplexState } from 'hook/helper.hook';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BANKING_VERTICAL } from 'types/mrph.vertical.constants';
import { startCognigyChat } from 'externals/_misc/cognigyChat';
import { useLogger } from 'hook/error.hook';

export const useEnrolledCashAccount = ({
  sesameCashAccount = {},
  eventHandler,
  userInfo,
}) => {
  const [state, setState] = useComplexState({
    frozenModalOpen: false,
    inactiveCardWarningModalOpen: false,
  });
  const logger = useLogger({ name: 'cashEnrolledHelper.hook' });
  const setFrozenModalOpen = v => setState({ frozenModalOpen: v });
  const { frozenModalOpen, inactiveCardWarningModalOpen } = state;
  const history = useHistory();
  const dispatch = useDispatch();
  const { receivedFirstFund, changedFirstPin, cardStatus } =
    sesameCashAccount !== null ? sesameCashAccount : {};

  const frozenCard = cardStatus === 'INACTIVE';
  const frozenSetPin = frozenCard && !changedFirstPin && receivedFirstFund;

  /* repeated logic */
  const updateDate = formatAsFullWithTime(new Date());

  const getDisplayBalance = () => {
    const balance = sesameCashAccount?.balance ? sesameCashAccount.balance : 0;
    if (balance < 0) {
      return formatNegativeIncome(Math.abs(balance), true);
    }
    return formatIncome(balance, true);
  };

  const displayBalance = getDisplayBalance();

  const showFundActionModule =
    sesameCashAccount && (!changedFirstPin || !receivedFirstFund);
  const showAccountOptions =
    (changedFirstPin && receivedFirstFund) || frozenSetPin;

  const handleFrozenModalOpen = e => {
    e?.preventDefault();
    setFrozenModalOpen(true);
  };

  const handleCloseFrozenModal = e => {
    e.preventDefault();
    eventHandler(CLICK, {
      'Click Type': 'Close',
      Vertical: BANKING_VERTICAL,
    });
    setFrozenModalOpen(false);
  };

  const handleManageCardClick = () => {
    eventHandler(CLICK, {
      'Click Type': 'Manage Card',
      Vertical: BANKING_VERTICAL,
    });
    if (sesameCashAccount && !sesameCashAccount.receivedFirstFund) {
      handleTransferBalanceModal({
        skipTracking: true,
        unfundedAccount: true,
      });
    } else {
      dispatch(
        requireTwoFactorAuthentication(history, history.location, {
          requireEnrollment: true,
          isCashEnroll: true,
        }),
      ).then(() => {
        history.push('/cash/manage');
      });
    }
  };

  const handleAccountOptionsClick = e => {
    const value = e.target.value;
    if (value === 'transferMoney') {
      handleTransferBalanceModal({
        type: 'moveMoney',
      });
    } else if (value === 'addMoney') {
      handleTransferBalanceModal({
        type: 'addMoney',
      });
    } else if (value === 'manageCard') {
      if (
        (sesameCashAccount.receivedFirstFund === false &&
          sesameCashAccount.cardStatus === 'INACTIVE') ||
        (sesameCashAccount.receivedFirstFund === true &&
          sesameCashAccount.cardStatus === 'ISSUED_INACTIVE')
      ) {
        setState({ inactiveCardWarningModalOpen: true });
      } else {
        handleManageCardClick();
      }
    }
  };

  const toggleInactiveCardWarningModal = () => {
    setState({ inactiveCardWarningModalOpen: !inactiveCardWarningModalOpen });
  };
  const onClickChat = () => {
    eventHandler(CLICK, {
      'Click Type': 'Customer Service Chat',
      Vertical: BANKING_VERTICAL,
    });
    if (userInfo) {
      startCognigyChat({ userInfo }).catch(e => {
        logger.showError(e.message);
      });
    }
  };

  const handleHowToTransferModal = () => {
    eventHandler(CLICK, {
      'Click Type': 'Add Money Video',
    });
    openModalWithUrlReplace(history, '/cashAccount/transferVideo');
  };

  const handleTransferBalanceModal = props => {
    const { skipTracking, unfundedAccount, type } = props;
    if (!skipTracking) {
      let clickType = type && type === 'moveMoney' ? 'Move Money' : 'Add Money';
      eventHandler(CLICK, {
        'Click Type': clickType,
        Vertical: BANKING_VERTICAL,
      });
    }
    if (unfundedAccount) {
      openModalWithUrlReplace(
        history,
        '/cashAccount/transferOptions?unfunded=true',
      );
    } else {
      openModalWithUrlReplace(history, '/cashAccount/transferOptions');
    }
  };

  const handleSetPinClick = () => {
    eventHandler(CLICK, {
      'Click Type': 'Set Card PIN',
      Vertical: BANKING_VERTICAL,
    });
    openModalWithUrlReplace(history, '/cashAccount/pin?pinType=Set');
  };

  return {
    updateDate,
    displayBalance,
    showFundActionModule,
    showAccountOptions,
    frozenCard,
    frozenSetPin,
    handleSetPinClick,
    handleAccountOptionsClick,
    handleHowToTransferModal,
    handleTransferBalanceModal,
    frozenModalOpen,
    handleFrozenModalOpen,
    handleCloseFrozenModal,
    inactiveCardWarningModalOpen,
    toggleInactiveCardWarningModal,
    onClickChat,
  };
};
